import React, { useState } from 'react';
import { API_URL, downloadAuthenticatedFile } from '../../config/api';

const TicketDetailModal = ({ isOpen, ticket, onClose, onAddNote }) => {
  const [copiedTrackLink, setCopiedTrackLink] = useState(false);
  const [quickNote, setQuickNote] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  if (!isOpen || !ticket) return null;

  const handleCopyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopiedTrackLink(true);
    setTimeout(() => setCopiedTrackLink(false), 2000);
  };

  const handleAddNoteSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!quickNote.trim() || isSubmittingNote || !onAddNote) return;

    setIsSubmittingNote(true);
    try {
      await onAddNote(ticket.id, quickNote.trim());
      setQuickNote('');
    } finally {
      setIsSubmittingNote(false);
    }
  };

  // Clean description display (strip raw webhook footer markdown if already parsed)
  const cleanDescription = ticket.description
    ? ticket.description.split('--- ### Submitter Details')[0].split('--- ### 👤 Submitter Details')[0].trim()
    : 'No description provided.';

  const getDynamicTrackingUrl = () => {
    if (ticket?.tracking_token) {
      const clientCode = ticket.client_code || (ticket.tracking_url ? ticket.tracking_url.split('/track/')[1]?.split('/')[0] : '') || '';
      return `${window.location.origin}/track/${clientCode}/${ticket.id}?token=${ticket.tracking_token}`;
    }
    if (ticket?.tracking_url) {
      try {
        const parsed = new URL(ticket.tracking_url);
        return `${window.location.origin}${parsed.pathname}${parsed.search}`;
      } catch (e) {
        return ticket.tracking_url;
      }
    }
    return null;
  };
  const trackingUrl = getDynamicTrackingUrl();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5">
      <div className="fixed inset-0" onClick={onClose} />
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 animate-fadeIn text-slate-800 flex flex-col max-h-[88vh]">
        
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 bg-blue-600 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shadow-2xs">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm sm:text-base font-bold text-white tracking-tight">Ticket Details</h3>
                <span className="font-mono text-[10px] bg-blue-700/80 px-2 py-0.5 rounded text-blue-100 font-semibold">
                  {ticket.ticket_number || (ticket.id ? `#${ticket.id.slice(0, 8)}` : 'TKT')}
                </span>
              </div>
              <p className="text-[11px] text-blue-100 line-clamp-1">
                {ticket.title}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xs transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 2-Column Responsive Body */}
        <div className="p-4 sm:p-5 overflow-y-auto grid grid-cols-1 lg:grid-cols-12 gap-4 text-xs">
          
          {/* LEFT COLUMN: Main Issue, Activity & Notes (7 cols) */}
          <div className="lg:col-span-7 space-y-3.5">
            
            {/* Primary Issue Card */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Issue Summary
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                  ticket.status === 'open' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                  ticket.status === 'in_progress' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  {ticket.status?.replace('_', ' ')}
                </span>
              </div>
              <h4 className="text-sm font-bold text-slate-900 leading-snug">{ticket.title}</h4>
              <p className="text-[11px] text-slate-600 leading-relaxed whitespace-pre-wrap">
                {cleanDescription}
              </p>
            </div>

            {/* Attached Media / Photo */}
            {ticket.media_url && (
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Attachment</span>
                <img
                  src={`${API_URL}${ticket.media_url}`}
                  alt="Ticket attachment"
                  className="max-h-48 rounded-lg border border-slate-200 object-contain bg-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(`${API_URL}${ticket.media_url}`, '_blank')}
                    className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-[11px] transition-colors shadow-2xs"
                  >
                    View Full Image
                  </button>
                  <button
                    onClick={() => downloadAuthenticatedFile(ticket.media_url, `ticket-${ticket.id}-attachment`)}
                    className="px-2.5 py-1 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold rounded-lg text-[11px] transition-colors shadow-2xs"
                  >
                    Download
                  </button>
                </div>
              </div>
            )}

            {/* Internal Notes & Activity Log */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2.5">
              <div className="flex items-center justify-between border-b border-slate-200/60 pb-1.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  Activity & Internal Notes ({ticket.notes?.length || 0})
                </span>
              </div>

              {ticket.notes && ticket.notes.length > 0 ? (
                <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                  {ticket.notes.map((note) => (
                    <div key={note.id} className="p-2 rounded-lg bg-white border border-slate-200/80 shadow-2xs space-y-0.5">
                      <div className="flex items-center justify-between text-[10px]">
                        <div className="flex items-center gap-1.5 font-bold text-slate-800">
                          <span>{note.created_by_username || 'Agent'}</span>
                          {note.created_by_role && (
                            <span className="px-1.5 py-0.2 rounded bg-indigo-50 text-indigo-700 font-semibold text-[9px] uppercase">
                              {note.created_by_role}
                            </span>
                          )}
                        </div>
                        <span className="text-slate-400 text-[10px]">
                          {note.created_at ? new Date(note.created_at).toLocaleString() : 'Recent'}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {note.content}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 italic py-1">No internal notes added yet.</p>
              )}

              {/* Quick Note Input */}
              {onAddNote && (
                <form onSubmit={handleAddNoteSubmit} className="pt-2 border-t border-slate-200/60 flex items-center gap-1.5">
                  <input
                    type="text"
                    placeholder="Type an internal note..."
                    value={quickNote}
                    onChange={(e) => setQuickNote(e.target.value)}
                    className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    type="submit"
                    disabled={!quickNote.trim() || isSubmittingNote}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg text-xs transition-colors shadow-2xs flex-shrink-0"
                  >
                    {isSubmittingNote ? 'Saving...' : 'Add Note'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Metadata, Submitter, Tracking URL (5 cols) */}
          <div className="lg:col-span-5 space-y-3.5">
            
            {/* Key Properties Card */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2.5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                Ticket Properties
              </span>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-white rounded-lg border border-slate-200/70">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Priority</span>
                  <span className={`font-bold capitalize text-xs ${
                    ticket.priority === 'high' ? 'text-red-600' :
                    ticket.priority === 'medium' ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {ticket.priority || 'medium'}
                  </span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-slate-200/70">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Category</span>
                  <span className="font-bold text-slate-800 capitalize text-xs">
                    {ticket.category?.replace('_', ' ') || 'Support'}
                  </span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-slate-200/70">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Created By</span>
                  <span className="font-bold text-slate-800 text-xs truncate block">
                    {ticket.created_by_username || 'User'}
                  </span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-slate-200/70">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Assignee</span>
                  <span className="font-bold text-slate-800 text-xs truncate block">
                    {ticket.assigned_to_username || 'Unassigned'}
                  </span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-slate-200/70 col-span-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Created At</span>
                  <span className="font-medium text-slate-700 text-[11px]">
                    {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : 'Recent'}
                  </span>
                </div>
              </div>
            </div>

            {/* Submitter Info Card */}
            {(ticket.submitter?.name || ticket.submitter?.email || ticket.submitter?.number) && (
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Submitter Details
                  </span>
                </div>
                <div className="p-2.5 bg-white rounded-lg border border-slate-200 text-xs space-y-1">
                  <div>
                    <span className="text-[10px] font-semibold text-slate-400 block">Name</span>
                    <span className="font-bold text-slate-800">{ticket.submitter.name || 'Not provided'}</span>
                  </div>
                  {ticket.submitter.email && (
                    <div>
                      <span className="text-[10px] font-semibold text-slate-400 block">Email</span>
                      <a href={`mailto:${ticket.submitter.email}`} className="text-blue-600 hover:underline font-medium">
                        {ticket.submitter.email}
                      </a>
                    </div>
                  )}
                  {ticket.submitter.number && (
                    <div>
                      <span className="text-[10px] font-semibold text-slate-400 block">Phone</span>
                      <a href={`tel:${ticket.submitter.number}`} className="text-blue-600 hover:underline font-medium">
                        {ticket.submitter.number}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Custom Attributes */}
            {ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0 && (
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Custom Attributes
                </span>
                <div className="grid grid-cols-2 gap-1.5">
                  {Object.entries(ticket.custom_fields).map(([key, val]) => (
                    <div key={key} className="p-2 bg-white rounded-lg border border-slate-200">
                      <span className="text-[9px] font-bold text-slate-400 uppercase block truncate">
                        {key}
                      </span>
                      <span className="text-[11px] font-bold text-slate-800 block truncate">
                        {String(val)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resolution Note (if closed) */}
            {ticket.close_comment && (
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-800">
                  Resolution Note
                </span>
                <p className="text-xs font-medium">{ticket.close_comment}</p>
              </div>
            )}

            {/* Customer Tracking URL */}
            {trackingUrl && (
              <div className="p-3 rounded-xl bg-blue-50/70 border border-blue-200 text-blue-950 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-900 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                    Customer Live Tracker URL
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    readOnly
                    value={trackingUrl}
                    className="w-full font-mono text-[10px] bg-white border border-blue-200 rounded-lg px-2 py-1 text-slate-800 select-all"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopyLink(trackingUrl)}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-[10px] font-bold flex-shrink-0"
                  >
                    {copiedTrackLink ? 'Copied' : 'Copy'}
                  </button>
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2 py-1 bg-white hover:bg-slate-100 border border-blue-200 text-blue-800 rounded-md text-[10px] font-bold flex-shrink-0 flex items-center gap-0.5"
                  >
                    <span>View</span>
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            )}

          </div>

        </div>

      </div>
    </div>
  );
};

export default TicketDetailModal;
